/**
 * ReorderControls — shared row gutter UI: [⬆][⋮⋮][⬇].
 *
 * Renders three small buttons in the row's left margin:
 *
 *   ⬆  Move up   — calls moveNodeUp; disabled at the top of the
 *                  parent's child list.
 *   ⋮⋮ Drag      — initiates HTML5 drag via useDragHandle. The
 *                  custom drop handler in TiptapStructuredEditor
 *                  picks up view.dragging on drop.
 *   ⬇  Move down — calls moveNodeDown; disabled at the bottom.
 *
 * Used by TopicView, ActionItemView, BulletNodeView,
 * AttendeeGroupView. The wrapper is `absolute -left-[60px]` so
 * the controls float in the page's left margin, outside the body
 * column — same spatial convention Notion / Tiptap demos use.
 *
 * The controls inherit the row's hover state via `group-hover/row`
 * so they fade in only when the user is actually mousing over
 * that row. focus-within keeps them visible while a button is
 * keyboard-focused.
 *
 * The bounds (canMoveUp / canMoveDown) re-derive on every editor
 * transaction so the buttons enable/disable correctly as the user
 * adds, removes, or moves siblings.
 */
import { useEffect, useRef, useState } from 'react';
import type { Editor } from '@tiptap/react';
import { ChevronUp, ChevronDown, GripVertical } from 'lucide-react';
import {
  moveNodeDown,
  moveNodeUp,
  reorderBounds,
} from '../reorder.ts';
import { useDragHandle } from '../dragHandle.ts';

interface Props {
  editor: Editor;
  getPos: () => number | undefined;
  /** Short noun for tooltips / aria-labels: "topic", "bullet",
   *  "action item", "attendee group". */
  label: string;
  /** Vertical offset from the row's top. Bullets / action items
   *  use 0; topics nudge down by 4 to align with the title's
   *  baseline. */
  topOffsetClass?: string;
}

export function ReorderControls({
  editor,
  getPos,
  label,
  topOffsetClass = 'top-0',
}: Props) {
  const handleRef = useRef<HTMLButtonElement>(null);
  useDragHandle(handleRef, editor, getPos);

  // Re-derive on every editor transaction so the up/down arrows
  // enable/disable correctly as the user adds, removes, or moves
  // siblings. Tiptap's `transaction` event fires for every state
  // change.
  const [bounds, setBounds] = useState({
    canMoveUp: false,
    canMoveDown: false,
  });
  useEffect(() => {
    const update = () => {
      const pos = getPos();
      if (typeof pos !== 'number') {
        setBounds({ canMoveUp: false, canMoveDown: false });
        return;
      }
      setBounds(reorderBounds(editor.view, pos));
    };
    update();
    editor.on('transaction', update);
    return () => {
      editor.off('transaction', update);
    };
  }, [editor, getPos]);

  if (!editor.isEditable) return null;

  const onMoveUp = () => {
    const pos = getPos();
    if (typeof pos === 'number') moveNodeUp(editor.view, pos);
  };
  const onMoveDown = () => {
    const pos = getPos();
    if (typeof pos === 'number') moveNodeDown(editor.view, pos);
  };

  // -left-[60px] keeps the trio inside the page's left margin
  // gutter (the page has 2.85" / ~273px of left padding on the
  // sheet, so 60px is comfortable).
  return (
    <div
      contentEditable={false}
      className={
        'absolute -left-[60px] z-10 flex items-center gap-0.5 ' +
        'opacity-0 transition-opacity ' +
        'group-hover/row:opacity-100 group-hover/topic:opacity-100 ' +
        'group-hover/bullet:opacity-100 focus-within:opacity-100 ' +
        topOffsetClass
      }
    >
      <ChevronButton
        icon={<ChevronUp size={11} strokeWidth={2.5} />}
        onClick={onMoveUp}
        disabled={!bounds.canMoveUp}
        title={`Move ${label} up`}
      />
      <button
        ref={handleRef}
        type="button"
        contentEditable={false}
        draggable
        title={`Drag to reorder ${label}`}
        aria-label={`Drag to reorder ${label}`}
        className={
          'flex h-5 w-5 cursor-grab items-center justify-center ' +
          'rounded text-charcoal transition-colors ' +
          'hover:bg-pine/10 hover:text-pine ' +
          'active:cursor-grabbing'
        }
      >
        <GripVertical size={12} strokeWidth={2.5} />
      </button>
      <ChevronButton
        icon={<ChevronDown size={11} strokeWidth={2.5} />}
        onClick={onMoveDown}
        disabled={!bounds.canMoveDown}
        title={`Move ${label} down`}
      />
    </div>
  );
}

function ChevronButton({
  icon,
  onClick,
  disabled,
  title,
}: {
  icon: React.ReactNode;
  onClick: () => void;
  disabled: boolean;
  title: string;
}) {
  return (
    <button
      type="button"
      contentEditable={false}
      onClick={onClick}
      disabled={disabled}
      title={title}
      aria-label={title}
      className={
        'flex h-4 w-5 items-center justify-center rounded ' +
        'text-charcoal transition-colors ' +
        'hover:bg-pine/10 hover:text-pine ' +
        'disabled:opacity-25 disabled:hover:bg-transparent disabled:hover:text-charcoal'
      }
    >
      {icon}
    </button>
  );
}
